import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { FIELD_QUANTITY } from '@components/dynamic-input-fields/src/utils';
import { createFieldsFromFormFields } from '@components/forms/utils/create-fields-from-form-fields';
import { FormField } from '@components/modals/add-holder-modal/src/add-holder-modal.type';
import { createMultiInviteTextRows } from '@components/process-invite-guest/utils/invite-guest-utils';
import { PROCESS_IMPORT_ROUTE } from '@constants/routes';
import { CompanyAuthContext } from '@contexts/company-auth-context/company-auth-context';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { EventContext } from '@contexts/event/event-context';
import { ModalContext } from '@contexts/modal/modal-context';
import { BOOKING_FORM_FIELDS, TICKET_HOLDER_FORM_FIELDS } from '@domain/admission';
import { ROLE_PROMOTER, ROLE_TEAM_LEADER } from '@domain/company';
import { useGetOfferAddon } from '@hooks/addons/use-get-offer-addons';
import { useGetAdmission } from '@hooks/admissions/use-get-admission';
import { useGetEvent } from '@hooks/events/use-get-event';
import { useImportMultiInvite } from '@hooks/import/use-import-multi-invite';
import { useAddGuest } from '@hooks/use-add-guest/use-add-guest';
import { IMPORT_TYPE_OFFLINE_SALE } from '@pages/events/utils/import-process-utils';

import { ProcessStepContent } from './components/process-step-content';
import {
  MULTIPLE_OFFLINE_SELL,
  OfflineSalesForm,
  ProcessOfflineSalesStep,
  SINGLE_OFFLINE_SELL,
  STEP_SELECT_TYPE,
  STEP_SUCCESS,
} from './process-offline-sales.type';

export const ProcessOfflineSales = () => {
  const formRef = useRef<HTMLFormElement>(null);
  const navigate = useNavigate();
  const { toggleModal, setPreventExit, handleCloseModal } = useContext(ModalContext);
  const [step, setStep] = useState<ProcessOfflineSalesStep>(STEP_SELECT_TYPE);
  const {
    eventState: { id: paramEventId, isLiveEvent },
  } = useContext(EventContext);

  const {
    companyState: {
      legacyId: companyLegacyId,
      defaultChannel: { id: defaultChannelId },
    },
    clubState: { slug: clubSlug },
  } = useContext(CompanyClubContext);
  const { role } = useContext(CompanyAuthContext);
  const isTeamLeaderOrPromoter = role && (role.name === ROLE_TEAM_LEADER || role.name === ROLE_PROMOTER);

  const methods = useForm<OfflineSalesForm>({
    defaultValues: {
      eventId: isLiveEvent ? paramEventId ?? '' : '',
      offerId: '',
      channelId: isTeamLeaderOrPromoter ? '' : defaultChannelId,
      quantity: 1,
      addOns: [],
      saleType: SINGLE_OFFLINE_SELL,
      bookingFormFields: {},
      ticketHolderFormFields: [],
    },
    mode: 'onChange',
  });

  const {
    watch,
    formState: { isDirty },
  } = methods;

  const offerId = watch('offerId');
  const email = watch('bookingFormFields.email');
  const quantity = watch(FIELD_QUANTITY);
  const addOns = watch('addOns');
  const eventId = watch('eventId');
  const channelId = watch('channelId');
  const saleType = watch('saleType');

  const { data: offer } = useGetAdmission({
    offerId,
    channelId,
    options: {
      enabled: !!offerId,
    },
  });
  const { data: event } = useGetEvent({ eventId });
  const { data: addons } = useGetOfferAddon({
    offerId: offer?.id,
    enabled: offer && offer?.addOns?.length > 0,
  });
  const { data: addGuestData, mutate: addGuest, isSuccess: addGuestSuccess } = useAddGuest({ hideToast: true });
  const {
    data: importMultiInviteData,
    mutate: importMultiInvite,
    isSuccess: importSuccess,
  } = useImportMultiInvite({ hideToast: true });

  const closeModal = useCallback(() => toggleModal({ isOpen: false, type: '' }), [toggleModal]);

  const handleSingleOfflineSubmit = (e: OfflineSalesForm) => {
    if (typeof e.addOns === 'string') e.addOns = [e.addOns];
    addGuest({
      formValue: {
        offerId: e.offerId,
        bookingFormFields: createFieldsFromFormFields<OfflineSalesForm>(e, BOOKING_FORM_FIELDS) as FormField[],
        ticketHolderFormFields: createFieldsFromFormFields<OfflineSalesForm>(
          e,
          TICKET_HOLDER_FORM_FIELDS
        ) as FormField[][],
        numTickets: e.quantity,
        channelId: e.channelId,
        addOns: e.addOns ? e.addOns.map((addon) => ({ id: addon, quantity: e.quantity })) : [],
      },
    });
  };

  const handleMultiOfflineSaleSubmit = (e: OfflineSalesForm) => {
    if (typeof e.addOns === 'string') e.addOns = [e.addOns];
    if (e.multiOfferText) {
      const rows = createMultiInviteTextRows(e.multiOfferText as string);
      const numTicketsQuantity = rows.reduce((acc, row) => acc + Number(row.numTickets), 0);
      importMultiInvite({
        importData: {
          offerId: e.offerId,
          channelId: e.channelId,
          eventId: e.eventId,
          rows,
          addOns: e.addOns ? e.addOns.map((addon) => ({ id: addon, quantity: numTicketsQuantity })) : [],
        },
      });
    }
  };

  const onSubmit = (e: OfflineSalesForm) => {
    if (saleType === SINGLE_OFFLINE_SELL) {
      handleSingleOfflineSubmit(e);
    } else if (saleType === MULTIPLE_OFFLINE_SELL) {
      handleMultiOfflineSaleSubmit(e);
    }
  };

  const createProcessPath = useCallback(
    (importId: string) => {
      if (!importId) return;
      return PROCESS_IMPORT_ROUTE.replace(':companyId', companyLegacyId)
        .replace(':clubSlug', clubSlug)
        .replace(':eventId', eventId)
        .replace(':importId', importId)
        .replace(':importType', IMPORT_TYPE_OFFLINE_SALE);
    },
    [companyLegacyId, clubSlug, eventId]
  );

  useEffect(() => {
    if (addGuestSuccess) {
      setStep(STEP_SUCCESS);
      setPreventExit(false);
    }
  }, [addGuestSuccess, setPreventExit]);

  useEffect(() => {
    setPreventExit(isDirty);
  }, [isDirty, setPreventExit]);

  useEffect(() => {
    if (importSuccess && importMultiInviteData) {
      const path = createProcessPath(importMultiInviteData?.importId);
      if (path) navigate(path);
      setPreventExit(false);
      closeModal();
    }
  }, [importSuccess, setPreventExit, importMultiInviteData, createProcessPath, eventId, navigate, closeModal]);

  return (
    <form className='flex justify-end flex-col relative' ref={formRef}>
      <FormProvider {...methods}>
        <ProcessStepContent
          {...{
            step,
            offer,
            event,
            saleType,
            quantity,
            addons: addons ?? [],
            addGuestData,
            addOns,
            email,
            closeModal,
            handleCloseModal,
            onSubmit,
            setStep,
          }}
        />
      </FormProvider>
    </form>
  );
};
